import { ChangeOrder, Lead, EnhancedDeal } from '../types';
import {
  SalesActivityTimeRange,
  filterLeadsByRange,
  filterDealsByRange,
  getDealCloseInstant,
  getRangeCutoff,
  isSalesActivityDeal,
  toLocalDateStr,
  rangeDayCount,
} from './leadAnalytics';
import { getSourceColor } from './sourceColors';
import { formatSourceName } from './sourceUtils';

export type ChartMode = 'leads' | 'sales' | 'both';

export interface TimelinePoint {
  periodKey: string;
  label: string;
  leads: { count: number; bySource: Record<string, number> };
  sales: {
    wonCount: number;
    lostCount: number;
    wonRevenue: number;
    changeOrderCount: number;
    changeOrderRevenue: number;
    designFees: number;
  };
}

/**
 * Approved change order on the sales timeline (approval day, not deal close).
 * Draft / sent / rejected / void never appear — only `status === 'approved'`
 * (portal signature or internal admin approval). That matches contract-value math.
 */
export interface SalesActivityChangeOrder {
  id: string;
  dealId: string;
  reference: string;
  title: string;
  clientName: string;
  projectName: string;
  deltaAmount: number;
  approvedAt: Date;
  /** Public change-order page slug (`/change-order/:slug`). */
  slug?: string;
  leadId?: string;
  projectId?: string;
}

export interface DesignFeePayment {
  amount: number;
  paidAt: Date;
  dealId?: string;
}

export interface SalesActivityPeriodLeadSource {
  source: string;
  count: number;
  color: string;
}

export interface SalesActivityPeriodDetail {
  periodKey: string;
  label: string;
  date: Date;
  leadsBySource: SalesActivityPeriodLeadSource[];
  leadCount: number;
  wonDeals: EnhancedDeal[];
  lostDeals: EnhancedDeal[];
  changeOrders: SalesActivityChangeOrder[];
  designFees: DesignFeePayment[];
  wonRevenue: number;
  changeOrderRevenue: number;
  designFeeTotal: number;
}

/**
 * Epoch ms for Date / Firestore Timestamp / `{seconds,_seconds}` maps / ISO strings.
 * Matches changeOrderSnapshot.changeOrderApprovedTime so CO dates survive
 * Timestamp-like shapes that `new Date(object)` would drop.
 */
export function parseSalesActivityInstant(value: unknown): number | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime();
  const maybe = value as { toDate?: () => Date; seconds?: number; _seconds?: number };
  if (typeof maybe.toDate === 'function') {
    try {
      const d = maybe.toDate();
      return d && !Number.isNaN(d.getTime()) ? d.getTime() : null;
    } catch {
      return null;
    }
  }
  const secs = typeof maybe.seconds === 'number' ? maybe.seconds : maybe._seconds;
  if (typeof secs === 'number') return secs * 1000;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/** Approval instant for an audit-log entry (Date / Timestamp / seconds map). */
function auditApprovalInstant(co: Pick<ChangeOrder, 'auditLog'>): number | null {
  const log = Array.isArray(co.auditLog) ? co.auditLog : [];
  for (let i = log.length - 1; i >= 0; i -= 1) {
    const entry = log[i];
    if (!entry) continue;
    if (entry.action !== 'signed' && entry.action !== 'internal_approved') continue;
    const ms = parseSalesActivityInstant(entry.at);
    if (ms != null) return ms;
  }
  return null;
}

/**
 * When the CO counts as sold — portal signature or internal approval.
 * Drafts and released-but-unsigned (`sent`) stay off the sales timeline.
 */
export function getChangeOrderApprovalInstant(
  co: Pick<ChangeOrder, 'status' | 'approvedAt' | 'signature' | 'auditLog'>,
): number | null {
  if (co.status !== 'approved') return null;
  return (
    parseSalesActivityInstant(co.approvedAt)
    || parseSalesActivityInstant(co.signature?.signedAt)
    || auditApprovalInstant(co)
  );
}

export function buildSalesActivityChangeOrders(
  changeOrders: ChangeOrder[],
  deals: EnhancedDeal[],
): SalesActivityChangeOrder[] {
  const dealById = new Map(deals.filter(d => d.id).map(d => [d.id as string, d]));
  const rows: SalesActivityChangeOrder[] = [];
  for (const co of changeOrders) {
    if (co.status !== 'approved') continue;
    const approvedMs = getChangeOrderApprovalInstant(co);
    if (approvedMs == null || !co.dealId) continue;
    const deal = dealById.get(co.dealId);
    const slug = typeof co.slug === 'string' ? co.slug.trim() : '';
    rows.push({
      id: co.id || `${co.dealId}-${co.number}`,
      dealId: co.dealId,
      reference: changeOrderReference(co),
      title: co.title || 'Change order',
      clientName: co.clientName || deal?.contactName || '',
      projectName: co.projectName || deal?.title || '',
      deltaAmount: Number(co.deltaAmount) || 0,
      approvedAt: new Date(approvedMs),
      slug: slug || undefined,
      leadId: deal?.leadId,
      projectId: co.projectId || undefined,
    });
  }
  return rows;
}

function filterChangeOrdersByRange(
  changeOrders: SalesActivityChangeOrder[],
  range: SalesActivityTimeRange,
): SalesActivityChangeOrder[] {
  const cutoff = getRangeCutoff(range);
  if (!cutoff) return changeOrders;
  return changeOrders.filter(co => co.approvedAt >= cutoff);
}

function changeOrdersInPeriod(
  changeOrders: SalesActivityChangeOrder[],
  bounds: { start: number; end: number },
  pipelineId: string | undefined,
  dealsById: Map<string, EnhancedDeal>,
): SalesActivityChangeOrder[] {
  return changeOrders.filter(co => {
    const t = co.approvedAt.getTime();
    if (t < bounds.start || t > bounds.end) return false;
    if (!pipelineId) return true;
    const deal = dealsById.get(co.dealId);
    return deal?.pipelineId === pipelineId;
  });
}

function formatSource(lead: Lead): string {
  return formatSourceName(lead.source || 'unspecified');
}

function changeOrderReference(co: Pick<ChangeOrder, 'reference' | 'projectNumber' | 'number'>): string {
  if (co.reference) return co.reference;
  const n = String(Math.max(1, Math.floor(co.number || 1))).padStart(3, '0');
  const label = `CO-${n}`;
  const pn = (co.projectNumber || '').trim();
  return pn ? `${pn}-${label}` : label;
}

/** Parse a `YYYY-MM-DD` period key as a local calendar day. */
export function parsePeriodKey(periodKey: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(periodKey);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

export function periodDayBounds(periodKey: string): { start: number; end: number } | null {
  const date = parsePeriodKey(periodKey);
  if (!date) return null;
  const start = date.getTime();
  return { start, end: start + 24 * 60 * 60 * 1000 - 1 };
}

function dealsClosedInPeriod(
  deals: EnhancedDeal[],
  bounds: { start: number; end: number },
  outcome: 'won' | 'lost',
  pipelineId?: string,
): EnhancedDeal[] {
  return deals.filter(deal => {
    if (!isSalesActivityDeal(deal)) return false;
    if (deal.outcome !== outcome) return false;
    if (pipelineId && deal.pipelineId !== pipelineId) return false;
    const close = getDealCloseInstant(deal);
    return close !== null && close >= bounds.start && close <= bounds.end;
  });
}

/**
 * Itemized leads / closed deals / design fees for one chart day.
 * Uses the same local-date rules as `buildSalesActivityTimeline` — no extra reads.
 */
export function listSalesActivityForPeriod(params: {
  leads: Lead[];
  deals: EnhancedDeal[];
  changeOrders: SalesActivityChangeOrder[];
  designFees: DesignFeePayment[];
  periodKey: string;
  pipelineId?: string;
}): SalesActivityPeriodDetail | null {
  const { leads, deals, changeOrders, designFees, periodKey, pipelineId } = params;
  const dealsById = new Map(deals.filter(d => d.id).map(d => [d.id as string, d]));
  const date = parsePeriodKey(periodKey);
  const bounds = periodDayBounds(periodKey);
  if (!date || !bounds) return null;

  const dayLeads = leads.filter(l => toLocalDateStr(new Date(l.createdAt)) === periodKey);
  const bySource = new Map<string, number>();
  dayLeads.forEach(l => {
    const src = formatSource(l);
    bySource.set(src, (bySource.get(src) || 0) + 1);
  });
  const leadsBySource = Array.from(bySource.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([source, count]) => ({
      source,
      count,
      color: getSourceColor(source),
    }));

  const wonDeals = dealsClosedInPeriod(deals, bounds, 'won', pipelineId);
  const lostDeals = dealsClosedInPeriod(deals, bounds, 'lost', pipelineId);
  const dayChangeOrders = changeOrdersInPeriod(changeOrders, bounds, pipelineId, dealsById);
  const dayFees = designFees.filter(f => {
    const t = f.paidAt.getTime();
    return t >= bounds.start && t <= bounds.end;
  });

  return {
    periodKey,
    label: `${date.getMonth() + 1}/${date.getDate()}`,
    date,
    leadsBySource,
    leadCount: dayLeads.length,
    wonDeals,
    lostDeals,
    changeOrders: dayChangeOrders,
    designFees: dayFees,
    wonRevenue: wonDeals.reduce((s, deal) => s + (deal.value || 0), 0),
    changeOrderRevenue: dayChangeOrders.reduce((s, co) => s + co.deltaAmount, 0),
    designFeeTotal: dayFees.reduce((s, f) => s + f.amount, 0),
  };
}

export function buildSalesActivityTimeline(params: {
  leads: Lead[];
  deals: EnhancedDeal[];
  changeOrders: SalesActivityChangeOrder[];
  designFees: DesignFeePayment[];
  range: SalesActivityTimeRange;
  pipelineId?: string;
}): TimelinePoint[] {
  const { leads, deals, changeOrders, designFees, range, pipelineId } = params;
  const filteredLeads = filterLeadsByRange(leads, range);
  const filteredDeals = filterDealsByRange(deals, range, pipelineId);
  const filteredChangeOrders = filterChangeOrdersByRange(changeOrders, range);
  const dealsById = new Map(deals.filter(d => d.id).map(d => [d.id as string, d]));
  let days = rangeDayCount(range, leads);
  // `all` bases the day window on the earliest lead; also stretch to cover
  // approved change orders so older COs are not clipped off the chart.
  if (range === 'all' && filteredChangeOrders.length) {
    const earliestCo = filteredChangeOrders.reduce(
      (min, co) => Math.min(min, co.approvedAt.getTime()),
      Date.now(),
    );
    const coDays = Math.min(
      Math.ceil((Date.now() - earliestCo) / (1000 * 60 * 60 * 24)) + 1,
      730,
    );
    days = Math.max(days, coDays);
  }
  const now = new Date();
  const points: TimelinePoint[] = [];

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    d.setHours(0, 0, 0, 0);
    const dateStr = toLocalDateStr(d);
    const label = `${d.getMonth() + 1}/${d.getDate()}`;

    const dayLeads = filteredLeads.filter(l => toLocalDateStr(new Date(l.createdAt)) === dateStr);
    const bySource: Record<string, number> = {};
    dayLeads.forEach(l => {
      const src = formatSource(l);
      bySource[src] = (bySource[src] || 0) + 1;
    });

    const dayStart = d.getTime();
    const dayEnd = dayStart + 24 * 60 * 60 * 1000 - 1;
    const wonDeals = filteredDeals.filter(deal => {
      if (deal.outcome !== 'won') return false;
      const close = getDealCloseInstant(deal);
      return close !== null && close >= dayStart && close <= dayEnd;
    });
    const lostDeals = filteredDeals.filter(deal => {
      if (deal.outcome !== 'lost') return false;
      const close = getDealCloseInstant(deal);
      return close !== null && close >= dayStart && close <= dayEnd;
    });
    const dayFees = designFees.filter(f => {
      const t = f.paidAt.getTime();
      return t >= dayStart && t <= dayEnd;
    });
    const dayChangeOrders = changeOrdersInPeriod(
      filteredChangeOrders,
      { start: dayStart, end: dayEnd },
      pipelineId,
      dealsById,
    );

    points.push({
      periodKey: dateStr,
      label,
      leads: { count: dayLeads.length, bySource },
      sales: {
        wonCount: wonDeals.length,
        lostCount: lostDeals.length,
        wonRevenue: wonDeals.reduce((s, deal) => s + (deal.value || 0), 0),
        changeOrderCount: dayChangeOrders.length,
        changeOrderRevenue: dayChangeOrders.reduce((s, co) => s + co.deltaAmount, 0),
        designFees: dayFees.reduce((s, f) => s + f.amount, 0),
      },
    });
  }

  return points;
}
